import { Button, Card, Col, message, Row, Table, Typography } from "antd";
import { useTranslation } from "react-i18next";
import RobinPage from "../components/core/RobinPage";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useGetAllMyUsersQuery,
  useGetMyPendingInvitationsQuery,
  useRemoveUserForPartnerMutation,
} from "../graphql/generated/hooks";
import { useState } from "react";
import InviteUserModal from "../components/InviteUserModal";
import EditDeleteActions from "../components/EditDeleteActions";

function SettingsUsers() {
  const { t } = useTranslation();
  const { data: users, loading, refetch } = useGetAllMyUsersQuery();
  const [removeUser, { loading: removing }] = useRemoveUserForPartnerMutation();
  const {
    data: invitations,
    loading: loadingInvitations,
    refetch: refetchInvitations,
  } = useGetMyPendingInvitationsQuery();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const userDataSource = users?.getAllUsersForPartner?.map((user, index) => {
    return {
      key: index,
      email: user.email,
      storeName: user.storeName,
      actions: (
        <EditDeleteActions
          onEdit={() => {}}
          onDelete={() => {
            removeUser({ variables: { id: user.id } })
              .then(() => {
                messageApi.success(t("message.success.data_deleted"));
                refetch();
              })
              .catch((error) => {
                messageApi.error(t("message.error.data_delete_failed"));
              });
          }}
          loadingDelete={removing}
        />
      ),
    };
  });

  const userColumns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      width: 280,
    },
    {
      title: "Store",
      dataIndex: "storeName",
      key: "storeName",
      render: (value: string | null | undefined) =>
        value || t("label.all_stores_access"),
      ellipsis: true,
      width: 200,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 88,
      align: "center" as const,
    },
  ];

  const invitationDataSource =
    invitations?.getPendingInvitationsForPartner?.map((invitation, index) => {
      return {
        key: index,
        email: invitation.email,
        storeName: invitation.storeName,
      };
    });

  const invitationColumns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      width: 260,
    },
    {
      title: "Store",
      dataIndex: "storeName",
      key: "storeName",
      render: (value: string | null | undefined) =>
        value || t("label.all_stores_access"),
      ellipsis: true,
      width: 180,
    },
  ];

  return (
    <RobinPage
      title={t("title.user_settings")}
      actions={[
        {
          label: (
            <Button
              type="text"
              onClick={() => {
                refetch();
                refetchInvitations();
              }}
              aria-label={t("button.refresh") || "Refresh"}
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </Button>
          ),
        },
        {
          label: (
            <Button
              type="primary"
              onClick={() => {
                setIsInviteModalOpen(true);
              }}
            >
              {t("button.invite_user")}
            </Button>
          ),
        },
      ]}
    >
      {contextHolder}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onInvited={() => refetchInvitations()}
        close={() => {
          setIsInviteModalOpen(false);
        }}
      />
      <Row gutter={[16, 16]} align="stretch">
        <Col xl={16} xs={24} style={{ display: "flex" }}>
          <Card
            title={<Typography.Text strong>{t("title.active_users")}</Typography.Text>}
            styles={{ body: { padding: 0 } }}
            style={{ width: "100%", height: "100%" }}
          >
            <Table
              loading={loading}
              dataSource={userDataSource}
              columns={userColumns}
              size="small"
              bordered={false}
              tableLayout="fixed"
              pagination={{ pageSize: 6, showSizeChanger: false }}
            />
          </Card>
        </Col>
        <Col xl={8} xs={24} style={{ display: "flex" }}>
          <Card
            title={<Typography.Text strong>{t("title.pending_invitations")}</Typography.Text>}
            styles={{ body: { padding: 0 } }}
            style={{ width: "100%", height: "100%" }}
          >
            <Table
              loading={loadingInvitations}
              dataSource={invitationDataSource}
              columns={invitationColumns}
              size="small"
              bordered={false}
              tableLayout="fixed"
              pagination={{ pageSize: 6, showSizeChanger: false }}
            />
          </Card>
        </Col>
      </Row>
    </RobinPage>
  );
}

export default SettingsUsers;
